import { Dictionary } from '@reduxjs/toolkit';
import { Slugs } from 'common/urls/Slugs';
import { chain, isNil } from 'lodash';
import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { Routes } from 'src/shared/Routes';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useSelector } from 'src/store';
import { isLoadedShef } from 'src/store/cart/entities';
import { CartLineItemV2 } from 'src/store/cart/types';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';

/**
 * This just generates a snippet of <span> text to encourage users to either
 *  1) Order more from their current relevant shef - we don't want to drive traffic _away_ from shefs
 *  2) Place multi-cart orders - this acts as a hint that it's possible
 */

// Util logic snippets
const shefIdForLatestUpdate = (lineItems: Dictionary<CartLineItemV2>): string | undefined =>
  chain(lineItems)
    .orderBy((item) => item?.updatedAt, ['desc'])
    .filter((item) => Boolean(item?.quantity))
    .map((item) => item?.shefId)
    .compact()
    .first()
    .value();

const getShefName = (shef) => {
  if (shef && isLoadedShef(shef)) {
    return shef?.shefProfile?.businessName ?? `Shef ${shef.name}`;
  }

  return null;
};

const MaybeLinkWrap: React.FC<{ to?: string; children: React.ReactNode }> = ({ to, children }) => {
  if (!to) {
    return <>{children}</>;
  }

  return (
    <Link className='cursor-pointer font-semibold underline' to={to}>
      {children}
    </Link>
  );
};

export interface IProps {
  formattedDiscount: string;
  meetTheMinimumAmount: string;
  className?: string;
}

export const MultiCartMinimumCta: React.FC<IProps> = ({ formattedDiscount, meetTheMinimumAmount, className }) => {
  const [cachedZipcode] = useCachedZipCode();

  const isOnExplorePage = !isNil(useRouteMatch(Routes.CONSUMER_EXPLORE));
  const isOnOrderPage = !isNil(useRouteMatch(Routes.CONSUMER_ORDER_ONE_TIME));

  const { activeCartShefId, lineItems, shefs } = useSelector((state) => ({
    shefs: state.cart.shefs,
    activeCartShefId: state.cart.activeCartShefId,
    lineItems: state.cart.lineItems,
  }));

  const { relevantShefId, relevantShefName, explorePath, shefPath } = useMemo(() => {
    const id = shefIdForLatestUpdate(lineItems.entities) ?? activeCartShefId;

    return {
      relevantShefId: id,
      relevantShefName: getShefName(shefs.entities[id]),
      explorePath: getSluggedPath(Routes.CONSUMER_EXPLORE, [{ slug: Slugs.ZIP_CODE, value: cachedZipcode ?? '' }]),
      shefPath: getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, [{ slug: Slugs.ID, value: id }]),
    };
  }, [activeCartShefId, cachedZipcode, lineItems.entities, shefs.entities]);

  // "You're $[xx.xx] away from free delivery! Add more from Shef [in cart] or mix and match from other shefs."
  return (
    <span className={className}>
      You're <span className='font-semibold'>{meetTheMinimumAmount}</span> away from {formattedDiscount} delivery! Add
      more{' '}
      {relevantShefName ? (
        <>
          from{' '}
          <MaybeLinkWrap to={!isOnOrderPage || activeCartShefId !== relevantShefId ? shefPath : undefined}>
            {relevantShefName}
          </MaybeLinkWrap>
          ,
        </>
      ) : (
        'dishes' // Dumb fallback juuuuust in case
      )}{' '}
      or mix and match from <MaybeLinkWrap to={!isOnExplorePage ? explorePath : undefined}>other shefs</MaybeLinkWrap>.
    </span>
  );
};
